import React, { createContext, useContext, useMemo, useState } from 'react';

interface SearchLocation {
  lat: number;
  lng: number;
}

interface SearchFiltersContextType {
  selectedInstrument: string;
  setSelectedInstrument: (value: string) => void;
  selectedSkillLevel: string;
  setSelectedSkillLevel: (value: string) => void;
  maxDistance: number;
  setMaxDistance: (value: number) => void;
  selectedGenres: string[];
  setSelectedGenres: React.Dispatch<React.SetStateAction<string[]>>;
  searchLocation: SearchLocation | null;
  setSearchLocation: (value: SearchLocation | null) => void;
  searchAddress: string | null;
  setSearchAddress: (value: string | null) => void;
}

const SearchFiltersContext = createContext<SearchFiltersContextType | undefined>(undefined);

export const SearchFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedInstrument, setSelectedInstrument] = useState('All Instruments');
  const [selectedSkillLevel, setSelectedSkillLevel] = useState('All Levels');
  const [maxDistance, setMaxDistance] = useState(50);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [searchLocation, setSearchLocation] = useState<SearchLocation | null>(null);
  const [searchAddress, setSearchAddress] = useState<string | null>(null);

  const value = useMemo(
    () => ({
      selectedInstrument,
      setSelectedInstrument,
      selectedSkillLevel,
      setSelectedSkillLevel,
      maxDistance,
      setMaxDistance,
      selectedGenres,
      setSelectedGenres,
      searchLocation,
      setSearchLocation,
      searchAddress,
      setSearchAddress,
    }),
    [
      maxDistance,
      searchAddress,
      searchLocation,
      selectedGenres,
      selectedInstrument,
      selectedSkillLevel,
    ]
  );

  return <SearchFiltersContext.Provider value={value}>{children}</SearchFiltersContext.Provider>;
};

export const useSearchFilters = (): SearchFiltersContextType => {
  const context = useContext(SearchFiltersContext);
  if (!context) {
    throw new Error('useSearchFilters must be used within a SearchFiltersProvider');
  }
  return context;
};
